import { useState } from 'react'
import type { FileMetadata } from '../../../../types'
import { apiClient } from '../lib/api-client'
import { folderLogger } from '../lib/logger'
import type { Feedback } from './use-feedback'

export interface PhotoActions {
  handleSetCover: () => Promise<void>
  handleDeletePhotos: () => void
  handleRenamePhoto: () => void
  handleRenameSubmit: () => Promise<void>
  renameModal: {
    isOpen: boolean
    value: string
    originalLabel: string
    loading: boolean
    setValue: (value: string) => void
    close: () => void
  }
}

// Set-cover, rename (including the `> 0` extension-split guard) and
// delete-photos (SOL-150 Task 9).
export function usePhotoActions(args: {
  currentFolderId: string | undefined
  albumFiles: FileMetadata[]
  selectedPhotos: Set<string>
  clearSelection: () => void
  refreshAlbumFiles: () => void
  fetchAlbums: (silent?: boolean) => Promise<void>
  feedback: Feedback
}): PhotoActions {
  const {
    currentFolderId, albumFiles, selectedPhotos, clearSelection,
    refreshAlbumFiles, fetchAlbums, feedback,
  } = args
  const { showNotification, closeConfirm, showConfirm, showToast } = feedback

  const handleDeletePhotos = () => {
    if (selectedPhotos.size === 0 || !currentFolderId) return

    const count = selectedPhotos.size
    const word = count === 1 ? 'photo' : 'photos'

    showConfirm(
      `Delete ${count} ${word}?`,
      `This action cannot be undone. The selected ${word} will be permanently removed.`,
      async () => {
        try {
          const fileIds = Array.from(selectedPhotos)
          await apiClient.deletePhotos(currentFolderId, fileIds)

          closeConfirm()
          showToast(`Deleted ${count} ${word}`)
          clearSelection()

          // Refresh the album files
          refreshAlbumFiles()
          // Also refresh the albums list in case isEmpty status changed
          fetchAlbums()
        } catch (error) {
          closeConfirm()
          folderLogger.error('Failed to delete photos', { error })
          showNotification('error', 'Delete Failed', 'Could not delete the selected photos. Please try again.')
        }
      },
      { variant: 'danger', confirmText: 'Delete' }
    )
  }

  // Rename modal state
  const [renameModalOpen, setRenameModalOpen] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [renameFileId, setRenameFileId] = useState('')
  const [renameOriginalLabel, setRenameOriginalLabel] = useState('')
  const [renameLoading, setRenameLoading] = useState(false)

  const handleSetCover = async () => {
    if (!currentFolderId || selectedPhotos.size !== 1) return

    const fileId = Array.from(selectedPhotos)[0]

    try {
      await apiClient.setAlbumCover(currentFolderId, fileId)
      showToast('Cover updated')
      clearSelection()
      // Refresh the opened album AND the album grid, since the card's cover
      // comes from the /api/albums list payload.
      refreshAlbumFiles()
      fetchAlbums()
    } catch (error) {
      folderLogger.error('Failed to set album cover', { error })
      showNotification('error', 'Cover Update Failed', 'Could not set the album cover. Please try again.')
    }
  }

  const handleRenamePhoto = () => {
    if (selectedPhotos.size !== 1) return

    const fileId = Array.from(selectedPhotos)[0]
    const fileMeta = albumFiles.find(f => f.id === fileId)
    if (!fileMeta) return

    setRenameFileId(fileId)
    setRenameOriginalLabel(fileMeta.label)

    // Extract name without extension for editing
    const label = fileMeta.label
    const lastDot = label.lastIndexOf('.')
    const nameWithoutExt = lastDot > 0 ? label.substring(0, lastDot) : label
    setRenameValue(nameWithoutExt)
    setRenameModalOpen(true)
  }

  const handleRenameSubmit = async () => {
    if (!currentFolderId || !renameFileId || !renameValue.trim() || renameLoading) return

    setRenameLoading(true)

    try {
      // Get the extension from original label
      const lastDot = renameOriginalLabel.lastIndexOf('.')
      const extension = lastDot > 0 ? renameOriginalLabel.substring(lastDot) : ''
      const newLabel = renameValue.trim() + extension

      await apiClient.renamePhoto(currentFolderId, renameFileId, newLabel)

      setRenameModalOpen(false)
      showToast(`"${renameOriginalLabel}" → "${newLabel}"`)
      clearSelection()

      // Refresh the album files
      refreshAlbumFiles()
    } catch (error) {
      folderLogger.error('Failed to rename photo', { error })
      showNotification('error', 'Rename Failed', 'Could not rename the photo. Please try again.')
    } finally {
      setRenameLoading(false)
    }
  }

  return {
    handleSetCover,
    handleDeletePhotos,
    handleRenamePhoto,
    handleRenameSubmit,
    renameModal: {
      isOpen: renameModalOpen,
      value: renameValue,
      originalLabel: renameOriginalLabel,
      loading: renameLoading,
      setValue: setRenameValue,
      close: () => setRenameModalOpen(false),
    },
  }
}
